import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { MutuallyExclusive } from '~/types/utils';

import { useClients } from '~/api/clients';
import { useCreateCreditNote } from '~/api/creditNotes';
import { useCreditableInvoices, useInvoice } from '~/api/invoices';
import { useUser } from '~/api/user';
import { Alert, Blocker, Container, Dialog, Header } from '~/components';
import { Combobox, Field, Textarea } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { FormNumberingSeries } from '~/features/documents';
import { useDialog } from '~/hooks/useDialog';
import { useIntl } from '~/hooks/useIntl';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { CreditNoteConfirmationData } from '../components/dialogs.types';
import type { CreateCreditNoteFormType } from './types';

import { CreditNoteConfirmationDialog } from '../components/CreditNoteConfirmationDialog';
import { columnStyle, rowStyle } from './CreateCreditNoteForm.css';
import { useInitialInvoiceId } from './CreateCreditNoteForm.hooks';
import { mapCreditNoteFormDataToPayload } from './utils';

export const CreateCreditNoteForm = () => {
  const initialInvoiceId = useInitialInvoiceId();

  const { data: creditableInvoices } = useCreditableInvoices();
  const { data: user } = useUser();
  const { data: clients } = useClients();
  const mutation = useCreateCreditNote();

  const navigate = useNavigate();
  const { formatConjunctiveList, formatCurrency } = useIntl();
  const { t } = useTranslation(['common', 'creditnotes', 'settings', 'validation']);

  const form = useForm<CreateCreditNoteFormType>({
    defaultValues: {
      invoiceId: initialInvoiceId,
      numberingSeriesId: user.numberingSeries.find((numberingSeries) => numberingSeries.enabledForCreditNotes)?.id || null,
      reason: '',
    },
  });
  const {
    control,
    formState: { isDirty },
    handleSubmit,
    setValue,
    watch,
  } = form;
  const watchInvoiceId = watch('invoiceId');
  const watchNumberingSeriesId = watch('numberingSeriesId');

  const { data: selectedInvoice } = useInvoice(watchInvoiceId);

  const invoicePaidAmounts = selectedInvoice?.coPaidAmounts ?? [];
  const client = useMemo(() => clients.find(({ id }) => id === selectedInvoice?.clientId) ?? null, [clients, selectedInvoice?.clientId]);

  const invoiceNumberingSeries = user.numberingSeries.find((series) => series.id === selectedInvoice?.numberingSeriesId);
  const selectedNumberingSeries = user.numberingSeries.find((series) => series.id === watchNumberingSeriesId);

  /** Auto select the numbering series id based on the selected invoice */
  useEffect(() => {
    if (invoiceNumberingSeries?.enabledForCreditNotes) setValue('numberingSeriesId', invoiceNumberingSeries.id);
  }, [invoiceNumberingSeries, setValue]);

  const confirmationDialog = useDialog<void, MutuallyExclusive<{ confirmed: true } & CreditNoteConfirmationData, { confirmed: false }>>();

  const onSubmit = handleSubmit(async (data) => {
    const { confirmed, fcEntryNumber, nextNumber } = await confirmationDialog.open();

    if (!confirmed) return;

    mutation.mutate(
      {
        ...mapCreditNoteFormDataToPayload(data),
        fcEntryNumber,
        nextNumber,
      },
      {
        onSuccess: (creditNote) => {
          toast.success({ description: t('creditnotes:alerts.successCreated') });
          navigate(r(routes.showInvoice, { invoiceId: creditNote.invoiceId }), { state: bypassBlocker });
        },
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  });

  const invoiceOptions = useMemo(
    () =>
      creditableInvoices.map((creditableInvoice) => ({
        value: creditableInvoice.id,
        label: creditableInvoice.fcEntryNumber,
      })),
    [creditableInvoices],
  );
  const invoiceItems = Combobox.useItems(invoiceOptions, { getLabel: (option) => option.label, getValue: (option) => option.value });

  return (
    <>
      <Header.Root>
        <Header.Content backTo={routes.creditNotes} title={t('creditnotes:create.title')} />

        <Header.Actions>
          <Header.Action icon="Send" loading={mutation.isPending} onClick={onSubmit}>
            {t('creditnotes:create.submit')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker isBlocked={isDirty} message={t('creditnotes:create.blocker')} />

      <Container>
        <form autoComplete="off" onSubmit={onSubmit}>
          <div className={rowStyle}>
            <div className={columnStyle}>
              {/* Invoice id */}
              <Controller
                control={control}
                name="invoiceId"
                render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                  <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                    <Field.Label>{t('creditnotes:fields.invoiceId.label')}</Field.Label>

                    <Combobox.Root items={invoiceItems} onValueChange={onChange} value={value}>
                      <Combobox.Button placeholder={t('creditnotes:fields.invoiceId.placeholder')} />
                      <Combobox.List>
                        {(option: (typeof invoiceOptions)[number]) => (
                          <Combobox.Item key={option.value} value={option.value}>
                            {option.label}
                          </Combobox.Item>
                        )}
                      </Combobox.List>
                    </Combobox.Root>

                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                  </Field.Root>
                )}
                rules={{ required: { value: true, message: t('validation:required') } }}
              />

              {/* Partial payments warning */}
              {invoicePaidAmounts.length > 0 && (
                <Alert announce icon="Warning" status="warning" title={t('creditnotes:create.partialPaymentsWarning.title')}>
                  {t('creditnotes:create.partialPaymentsWarning.message', {
                    count: invoicePaidAmounts.length,
                    payment: formatConjunctiveList(invoicePaidAmounts.map(({ amount }) => formatCurrency(amount))),
                  })}
                </Alert>
              )}

              {/* Numbering series */}
              <FormNumberingSeries enabledFor="creditNotes" form={form} />
            </div>

            {/* Column 2 — reason */}
            <div className={columnStyle}>
              <Controller
                control={control}
                name="reason"
                render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                  <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                    <Field.Label>{t('creditnotes:fields.reason.label')}</Field.Label>
                    <Textarea
                      onBlur={onBlur}
                      onValueChange={onChange}
                      placeholder={t('creditnotes:fields.reason.placeholder')}
                      ref={ref}
                      rows={4}
                      value={value}
                    />

                    <Field.Footer>
                      <Field.Description>{t('creditnotes:fields.reason.helpText')}</Field.Description>
                      <Field.Error match={!!error}>{error?.message}</Field.Error>
                    </Field.Footer>
                  </Field.Root>
                )}
                rules={{
                  maxLength: {
                    value: 2000,
                    message: t('validation:maxLength', { attribute: t('creditnotes:fields.reason.label'), max: 2000 }),
                  },
                  required: { value: true, message: t('validation:required') },
                  validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                }}
              />
            </div>
          </div>
        </form>
      </Container>

      {/* Confirmation modal */}
      <Dialog.Root onOpenChange={() => confirmationDialog.close({ confirmed: false })} open={confirmationDialog.isOpen}>
        {confirmationDialog.isOpen && selectedNumberingSeries && (
          <CreditNoteConfirmationDialog
            disabled={mutation.isPending}
            numberingSeries={selectedNumberingSeries}
            onConfirm={(data) => confirmationDialog.close({ confirmed: true, ...data })}
            peppol={!!client?.peppolId}
          />
        )}
      </Dialog.Root>
    </>
  );
};
